import { getFramePositions, getFrameScaling } from "shared/utils/door";
import type { VerticalBeamPosition } from "entities/configurator/model/layoutMeta";
import { GeometryBounds2D } from "../components/openingGeometry";

export interface FrameEdges {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface FrameEdgesInput {
  width: number;
  height: number;
  leftBounds: GeometryBounds2D;
  rightBounds: GeometryBounds2D;
  topBounds: GeometryBounds2D;
  bottomBounds: GeometryBounds2D;
  frameTopScaleY?: number;
  frameTopShiftY?: number;
}

export const getVerticalFrameEdges = ({
  width,
  height,
  leftBounds,
  rightBounds,
  topBounds,
  bottomBounds,
  frameTopScaleY,
  frameTopShiftY,
}: FrameEdgesInput): FrameEdges => {
  const framePositions = getFramePositions(width, height);
  const frameScaling = getFrameScaling(width, height);

  return {
    left: leftBounds.minX * frameScaling.vertical.x + framePositions.left.x,
    right: rightBounds.maxX * frameScaling.vertical.x + framePositions.right.x,
    top:
      topBounds.minY * (frameTopScaleY ?? frameScaling.horizontal.y) +
      framePositions.top.y +
      (frameTopShiftY ?? 0),
    bottom:
      bottomBounds.maxY * frameScaling.horizontal.y + framePositions.bottom.y,
  };
};

export const getLocalVerticalBeamX = (
  position: VerticalBeamPosition,
  left: number,
  right: number,
  beam: Pick<GeometryBounds2D, "minX" | "maxX">,
  {
    insetMeters,
    parentScaleX = 1,
  }: { insetMeters: number; parentScaleX?: number },
): number => {
  const inset = insetMeters / parentScaleX;

  if (position === "left") {
    return left + inset - beam.maxX / parentScaleX;
  }

  if (position === "right") {
    return right - inset - beam.minX / parentScaleX;
  }

  return (left + right) / 2 - (beam.minX + beam.maxX) / (2 * parentScaleX);
};
